import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import moment from 'moment'
import { filter, forkJoin, interval, Subscription, takeWhile } from 'rxjs'
import { ClientInformationAttribute } from 'src/app/models/client-information/ClientInformationAttribute'
import { ClientDto } from 'src/app/models/client/client-dto'
import { Filter } from 'src/app/models/power-bi/filter'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { CommonService } from 'src/app/services/common/common.service'
import { FeaturesService } from 'src/app/services/features/features.service'
import { PreviousRouteService } from 'src/app/services/previous-route/previous-route.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'

@Component({
  selector: 'app-client-dashboard',
  templateUrl: './client-dashboard.component.html',
  styleUrls: ['./client-dashboard.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientDashboardComponent implements OnInit, OnDestroy {
  _autoRefresh: boolean = false
  _clientEditEnabled: boolean
  _clientInformationTitle = 'Client Information'
  _clientInformationDisplayText = ''
  _clientInformationUrl = ''
  _clientInformationAttributes: ClientInformationAttribute[] =
    this.commonService.createEmptyAttributeList()
  _clientInFocus: string = ''
  _welcomeMessage: string = ''
  _clientDetails: ClientDto
  clientId: string = ''
  _age: string = ''
  _dob: Date
  _flipFilters: Filter[]
  _reddiFilters: Filter[]
  _totalReviews = 0
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
    servicePlanHasAccess: false,
  }
  private refreshTimerSub?: Subscription
  private navSub?: Subscription

  constructor(
    private route: ActivatedRoute,
    private clientService: ClientService,
    public featureService: FeaturesService,
    protected spinnerService: SpinnerService,
    public router: Router,
    private bannerService: BannerService,
    private previousRouteService: PreviousRouteService,
    private commonService: CommonService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Check if we should start auto-refresh from navigation state
    const state =
      this.router.getCurrentNavigation()?.extras?.state || history.state

    if (state?.['startAutoRefresh']) {
      this.startAutoRefresh()
    }

    this.navSub = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        // Check for state after navigation completes
        if (history.state?.['startAutoRefresh']) {
          this.startAutoRefresh()
        }
      })

    setTimeout(() => this.processData())
  }

  routerLinkClicked() {
    this.previousRouteService.clearPreviousUrl()
    this.previousRouteService.goToPreviousUrl('/dashboard')
  }

  setClientInformation(client: ClientDto) {
    this._clientInFocus = client.contactId
    this._clientInformationDisplayText = this._clientEditEnabled
      ? 'Edit profile'
      : ''
    this._clientInformationUrl = this._clientEditEnabled
      ? '/edit-client/' + client.contactId
      : ''

    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Age',
      client.age,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'DOB',
      moment(client.dateOfBirth).format('Do MMMM, YYYY'),
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Gender',
      client.genderCodeName,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Case Manager',
      client.clientCaseManagerName,
    )
  }

  redirect(destination: string) {
    this.router.navigate([destination])
  }

  navigateTo(target: string) {
    this.commonService.navigateTo(target, this.clientId)
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._clientDetails,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._clientDetails,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._clientDetails,
    )
  }

  retrieveIndicators(client: ClientDto) {
    this._totalReviews = this.commonService.returnReviewIndicator(client)
  }

  previousRoute() {
    const prevUrl = this.previousRouteService.getPreviousUrl()
    if (prevUrl) {
      this.previousRouteService.goToPreviousUrl(prevUrl)
    }
  }

  private processData(): void {
    this.spinnerService.show()

    this.route.paramMap.subscribe((paramMap) => {
      this.clientId = paramMap.get('clientId')

      // Validate clientId
      if (!this.commonService.isValidUUID(this.clientId)) {
        this.bannerService.showBanner(
          `Invalid clientId ${this.clientId}.`,
          'error',
          30000,
        )
        this.spinnerService.hide()
        throw new Error('Invalid ClientId')
      }

      // Set up filters
      this._flipFilters = [
        {
          attribute: 'Client/contactid',
          operation: 'eq',
          value: this.clientId,
        },
      ]

      this._reddiFilters = [
        {
          attribute: 'Client/contactid',
          operation: 'eq',
          value: this.clientId,
        },
      ]

      let request = {
        clientDetails: this.clientService.Details(this.clientId),
        features: this.featureService.GetFeatures(),
      }

      forkJoin(request).subscribe({
        next: ({ clientDetails, features }) => {
          this._clientEditEnabled = this.commonService.getFeatureValue(
            features,
            'clientEdit',
          )
          this._hasAccess.servicePlanHasAccess =
            this.commonService.getFeatureValue(features, 'servicePlan')
          this._clientDetails = clientDetails
          this._welcomeMessage = this._clientDetails.preferredFullName
          this._age = this._clientDetails.age
          this._dob = this._clientDetails.dateOfBirth
          this.checkToolsAccess()
          this.retrieveIndicators(this._clientDetails)
          this.setClientInformation(clientDetails)
          this.cd.markForCheck()
        },
        error: (error: Error) => {
          this.spinnerService.hide()
          this.bannerService.showBanner(
            'An error occurred while retrieving the reference data.',
            'error',
            30000,
          )
          throw error
        },
        complete: () => {
          this.spinnerService.hide()
        },
      })
    })
  }

  private startAutoRefresh() {
    this._autoRefresh = true

    // Stop any previous timer
    if (this.refreshTimerSub) this.refreshTimerSub.unsubscribe()

    // Auto-refresh for x seconds
    const start = Date.now()

    this.refreshTimerSub = interval(this.commonService.getRefreshTimeOut())
      .pipe(
        takeWhile(
          () => Date.now() - start < this.commonService.getRefreshLapse(),
        ),
      )
      .subscribe({
        next: () => {
          this._autoRefresh = true
          this.cd.markForCheck()
        },
        complete: () => {
          this._autoRefresh = false
          this.cd.markForCheck()
        },
      })
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe()
    this.refreshTimerSub?.unsubscribe()
  }
}
